import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  ExpandMore,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import userIcon from "../../../static/media/icon/user.svg";
import briefcaseIcon from "../../../static/media/icon/briefcase.svg";

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) {
      return decodeURIComponent(t.substring(name.length + 1));
    }
  }
  return "";
};

const fetchCsrfToken = async () => {
  const fromCookie = getCSRFTokenFromCookie();
  if (fromCookie) return fromCookie;

  try {
    const r = await fetch("/sistema/get-csrf-token/", {
      method: "GET",
      credentials: "include",
    });
    const d = await r.json().catch(() => ({}));
    return d?.csrfToken || d?.csrf || d?.token || "";
  } catch {
    return "";
  }
};

const TopBar = ({ open, setOpen }) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [email, setEmail] = useState(localStorage.getItem("user_email") || "");
  const [role, setRole] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [empresa, setEmpresa] = useState("");
  const [ambiente, setAmbiente] = useState("2");

  const navigate = useNavigate();

  useEffect(() => {
    const s = localStorage.getItem("is_superuser") === "true";
    const a = localStorage.getItem("is_admin") === "true";
    const v = localStorage.getItem("is_vendedor") === "true";

    if (s) {
      setRole("SUPERUSUARIO");
      setIsAdmin(true);
    } else if (a) {
      setRole("ADMINISTRADOR");
      setIsAdmin(true);
    } else if (v) {
      setRole("VENDEDOR");
      setIsAdmin(false);
    } else {
      setRole("");
      setIsAdmin(false);
    }

    const fetchSistema = async () => {
      try {
        const res = await fetch("/facturas/empresa/info/", {
          credentials: "include",
        });

        if (!res.ok) return;

        const data = await res.json().catch(() => ({}));
        const nc = String(data?.nombre_comercial || "").trim();
        const rs = String(data?.razon_social || "").trim();
        const em = String(data?.empresa || "").trim();

        setEmpresa(nc || rs || em || "");
        setAmbiente(String(data?.ambiente || "2").trim() === "1" ? "1" : "2");
      } catch {}
    };

    fetchSistema();

    if (!email) {
      const fetchUser = async () => {
        try {
          const csrf = await fetchCsrfToken();
          const res = await fetch("/sistema/get_user_info/", {
            method: "GET",
            credentials: "include",
            headers: csrf ? { "X-CSRFToken": csrf } : {},
          });

          if (!res.ok) return;

          const data = await res.json().catch(() => ({}));

          if (data?.email) {
            localStorage.setItem("user_email", data.email);
            setEmail(data.email);
          }

          const ss = !!data?.is_superuser;
          const aa = !!data?.is_admin;
          const vv = !!data?.is_vendedor;

          if (ss) {
            setRole("SUPERUSUARIO");
            setIsAdmin(true);
          } else if (aa) {
            setRole("ADMINISTRADOR");
            setIsAdmin(true);
          } else if (vv) {
            setRole("VENDEDOR");
            setIsAdmin(false);
          } else {
            setRole("");
            setIsAdmin(false);
          }

          localStorage.setItem("is_superuser", String(ss));
          localStorage.setItem("is_admin", String(aa));
          localStorage.setItem("is_vendedor", String(vv));
        } catch {}
      };

      fetchUser();
    }
  }, [email]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      const csrf = await fetchCsrfToken();
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
      });
    } catch {}

    localStorage.clear();
    navigate("/");
  };

  const dotColor = ambiente === "2" ? "#4CAF50" : "#F44336";

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 1, sm: 2 },
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        background: "#fafafa",
        borderBottom: "1px solid #e5e7eb",
        minHeight: 72,
        position: "sticky",
        top: 0,
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.2, sm: 0.4 },
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Tooltip title={open ? "Cerrar" : "Abrir"} arrow>
          <IconButton onClick={() => setOpen(!open)} size="small">
            {isMobile ? (
              <MenuIcon sx={{ fontSize: 28, color: "black" }} />
            ) : open ? (
              <ChevronLeft sx={{ fontSize: 30, color: "black" }} />
            ) : (
              <ChevronRight sx={{ fontSize: 30, color: "black" }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Panel de control" arrow>
          <IconButton onClick={() => navigate("/admin-panel")} size="small">
            <Dashboard sx={{ fontSize: 28, color: "black" }} />
          </IconButton>
        </Tooltip>

        {!isMobile && !!role && (
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: 700,
              color: "#444",
              ml: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            {role}
          </Typography>
        )}
      </Box>

      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#1565C0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {empresa || "—"}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.6, sm: 1.2 },
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {isAdmin && (
          <Tooltip title="Mi Compañía" arrow>
            <Box
              onClick={() => navigate("/admin-panel/empresa")}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                px: 0.6,
                py: 0.5,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              <img
                src={briefcaseIcon}
                alt="Compañía"
                style={{
                  width: 28,
                  height: 28,
                }}
              />
            </Box>
          </Tooltip>
        )}

        <Box
          onClick={handleMenuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            px: { xs: 0.7, sm: 1 },
            py: 0.5,
            borderRadius: "12px",
            minWidth: 0,
            "&:hover": {
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <img
              src={userIcon}
              alt="Usuario"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotColor,
                border: "2px solid white",
              }}
            />
          </Box>

          {!isMobile && (
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "#1565C0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 220,
              }}
            >
              {email || "—"}
            </Typography>
          )}

          <ExpandMore sx={{ color: "#1565C0", flexShrink: 0 }} />
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {isMobile && <MenuItem disabled>{email || "—"}</MenuItem>}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/admin-panel/perfil");
          }}
        >
          Mi perfil
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
        >
          Cerrar Sesión
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopBar;